import { extractAllBlocksFromTpl, getFilterKeyForBlock } from "@okf/ootils";
import { _getRollupOptions } from "./_getRollupOptions";


export const _extractOptionsFromContentTypes = ({contentTypes, allTpls, showNumberInputsInOptions}) => {
  if (!Array.isArray(contentTypes) || contentTypes.length === 0) return [];


  let allOptions = [];

  // Loop through each content type
  for (const contentType of contentTypes) {
    const thisCTTpl = allTpls.find(tpl => tpl.kp_content_type === contentType);
    if (!thisCTTpl) continue; // Skip if template not found

    const thisCTBlocks = extractAllBlocksFromTpl({tpl: thisCTTpl});
    const thisCTTagBlocks = thisCTBlocks.filter(bl => bl.valuePath.startsWith("tags."));
    const thisCTStaticSingleAndMultiSelectBlocks = thisCTBlocks.filter(bl => ['DropdownSingle', 'DropdownMulti', 'RadioList', 'CheckboxList'].includes(bl.comp));
    const thisCTGroupQuestionsBlocks = thisCTBlocks.filter(bl => bl.comp === "GroupQuestionsInputSingle");
    const thisCTGroupQuestionsBlocksPlusSplit = thisCTGroupQuestionsBlocks.flatMap(bl => {
      const split = bl.props.subQuestionLabels.map(ss => ({
        comp: bl.comp + ' (Sub-Question)',
        valuePath: bl.valuePath + '.' + ss.value,
        props: {
          label: bl.props?.label + ' - ' + ss.display,
          shortLabel: bl.props?.shortLabel
            ? bl.props?.shortLabel + ' - ' + ss.display
            : undefined,
        }
      }))
      return [
        bl,
        ...split
      ]
    })
    // showNumberInputsInOptions is always true now — the backend (aggrMetricsAcrossDimensions)
    // auto-buckets NumberInput fields into categorical ranges via createNumberInputBucketingStages.
    const thisCTNumberBlocks = showNumberInputsInOptions === false ? [] : thisCTBlocks.filter(bl => bl.comp === 'NumberInput');

    // System/Meta fields (fields not defined in template but available on all content)
    const systemFieldOptions = [
      {
        display: 'Document Author',
        // desc: 'User Reference',
        value: `${contentType}__meta.kp_contributed_by`,
        valuePath: 'meta.kp_contributed_by',
        contentType,
        // Matches the "Author" filter in commonDocumentFilters, which keys on
        // the same valuePath (valuePathType keys ignore scope).
        filterKey: getFilterKeyForBlock({ block: { valuePath: 'meta.kp_contributed_by' } }),
      }
    ];

    // Normal options
    const normalOptionsToReturn = [...thisCTTagBlocks, ...thisCTStaticSingleAndMultiSelectBlocks, ...thisCTGroupQuestionsBlocksPlusSplit, ...thisCTNumberBlocks].map(d => ({
      display: d.props.shortLabel || d.props.label || d.valuePath,
      desc: d.comp,
      value: `${contentType}__${d.valuePath}`,
      valuePath: d.valuePath,
      comp: d.comp,
      contentType,
      // The generated filter for this same field carries an identical key
      // (buildFilterConfigurations stamps it from the same helper), so anything
      // needing "the filter equivalent of this option" can just match on it —
      // no re-deriving the queryable path per field type.
      filterKey: getFilterKeyForBlock({ block: d, scope: "tags" }),
    }));

    // Rollup possibilities
    const rollupOptionsToReturn = _getRollupOptions({tagBlocksOnThisTpl: thisCTTagBlocks, allTpls, thisContentType: contentType});

    // Add options from this content type to the main array
    allOptions = [
      ...allOptions,
      ...systemFieldOptions,
      ...normalOptionsToReturn,
      ...rollupOptionsToReturn
    ];
  }
  
  // Dedup by option.value (keeps first occurrence).
  //
  // Why this exists: getRollupPossibilities walks tag chains recursively, and the same
  // target field can be reachable via multiple chains from one source tpl (e.g. two tag
  // blocks on tpl A both eventually point at tpl C's `gender`). Without dedup, `gender`
  // would appear 2-3 times in the same dropdown group with identical display.
  //
  // CRITICAL: rollup option `value`s are namespaced as `${sourceTpl}__VIA__${targetTpl}__${valuePath}`
  // (see _getRollupOptions.js). Real field `value`s are `${tpl}__${valuePath}`. This
  // distinction matters: previously rollup values were just `${targetTpl}__${valuePath}`,
  // which collided with the target tpl's OWN field option. Since rollups were emitted
  // first (during the source tpl's loop iteration), the dedup kept the rollup and
  // dropped the real field — the field then appeared under the WRONG group in the UI
  // (segrigateDocs by contentType put it under the source tpl, not the target tpl).
  // The VIA prefix fixes that while still deduping legitimate same-source same-target
  // chains.
  const seenValues = new Set();
  const uniqueOptions = allOptions.filter(option => {
    if (seenValues.has(option.value)) {
      return false;
    }
    seenValues.add(option.value);
    return true;
  });
  
  return uniqueOptions;
};